import React, { useState, useEffect, useMemo } from 'react';
import { getApiBase } from '../api';

function money(cents) {
  if (cents === null || cents === undefined) return '—';
  return `$${(Number(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
}

function formatDate(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

const statusStyles = {
  PENDING: { bg: '#fef3c7', text: '#92400e', label: '⏳ Pending' },
  APPROVED: { bg: '#dbeafe', text: '#1e40af', label: '✓ Approved' },
  FUNDED: { bg: '#d1fae5', text: '#065f46', label: '💵 Funded' },
  SETTLED: { bg: '#e0e7ff', text: '#3730a3', label: '✅ Settled' },
  REJECTED: { bg: '#fee2e2', text: '#991b1b', label: '✗ Rejected' },
  CANCELLED: { bg: '#f3f4f6', text: '#6b7280', label: 'Cancelled' }
};

const tierBadges = {
  GOLD: '🥇',
  SILVER: '🥈', 
  BRONZE: '🥉',
  NEW: '🆕'
};

export default function BrokerFactoringDashboard() {
  const apiBase = useMemo(() => getApiBase(), []);
  
  const [stats, setStats] = useState(null);
  const [allRequests, setAllRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [filter, setFilter] = useState('active'); // active | all | pending | approved | funded | settled

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    setLoading(true);
    setError('');
    try {
      const [statsRes, pendingRes] = await Promise.all([
        fetch(`${apiBase}/factoring/admin/dashboard`),
        fetch(`${apiBase}/factoring/admin/pending?take=100`)
      ]);
      
      const statsData = await statsRes.json();
      const pendingData = await pendingRes.json();
      
      if (!statsRes.ok) throw new Error(statsData.error);
      if (!pendingRes.ok) throw new Error(pendingData.error);
      
      setStats(statsData);
      
      // For now, we only have pending endpoint - let's fetch all via a different approach
      // We'll need to add an "all requests" endpoint, but for now use pending
      setAllRequests(pendingData.requests || []);
      
      // Fetch approved and funded too by checking the database directly
      await fetchAllRequests();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function fetchAllRequests() {
    try {
      // Fetch all active requests (pending, approved, funded)
      const res = await fetch(`${apiBase}/factoring/admin/all?take=100`);
      if (res.ok) {
        const data = await res.json();
        setAllRequests(data.requests || []);
      }
    } catch (err) {
      // Fallback - endpoint might not exist yet
      console.log('All requests endpoint not available');
    }
  }

  async function handleApprove(requestId) {
    setActionLoading(requestId);
    try {
      const res = await fetch(`${apiBase}/factoring/admin/approve/${requestId}`, {
        method: 'POST'
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      await fetchData();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  }

  async function handleReject(requestId) {
    const reason = prompt('Rejection reason (optional):');
    if (reason === null) return; // cancelled
    
    setActionLoading(requestId);
    try {
      const res = await fetch(`${apiBase}/factoring/admin/reject/${requestId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      await fetchData();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  }

  async function handleFund(requestId) {
    if (!window.confirm('Mark this request as funded? This confirms money was sent to the carrier.')) return;
    
    setActionLoading(requestId);
    try {
      const res = await fetch(`${apiBase}/factoring/admin/fund/${requestId}`, {
        method: 'POST'
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      await fetchData();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  }

  async function handleSettle(requestId) {
    if (!window.confirm('Mark this request as settled? This confirms the broker/shipper has paid.')) return;
    
    setActionLoading(requestId);
    try {
      const res = await fetch(`${apiBase}/factoring/admin/settle/${requestId}`, {
        method: 'POST'
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      await fetchData();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  }

  function renderActionButtons(req) {
    const isLoading = actionLoading === req.id;
    
    switch (req.status) {
      case 'PENDING':
        return (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => handleApprove(req.id)}
              disabled={isLoading}
              style={{
                padding: '6px 12px',
                background: '#059669',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                fontWeight: 600,
                cursor: isLoading ? 'not-allowed' : 'pointer'
              }}
            >
              {isLoading ? '...' : '✓ Approve'}
            </button>
            <button
              onClick={() => handleReject(req.id)}
              disabled={isLoading}
              style={{
                padding: '6px 12px',
                background: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                fontWeight: 600,
                cursor: isLoading ? 'not-allowed' : 'pointer'
              }}
            >
              {isLoading ? '...' : '✗ Reject'}
            </button>
          </div>
        );
      case 'APPROVED':
        return (
          <button
            onClick={() => handleFund(req.id)}
            disabled={isLoading}
            style={{
              padding: '6px 12px',
              background: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              fontWeight: 600,
              cursor: isLoading ? 'not-allowed' : 'pointer'
            }}
          >
            {isLoading ? '...' : '💵 Mark Funded'}
          </button>
        );
      case 'FUNDED':
        return (
          <button
            onClick={() => handleSettle(req.id)}
            disabled={isLoading}
            style={{
              padding: '6px 12px',
              background: '#7c3aed',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              fontWeight: 600,
              cursor: isLoading ? 'not-allowed' : 'pointer'
            }}
          >
            {isLoading ? '...' : '✅ Mark Settled'}
          </button>
        );
      default:
        return <span style={{ color: '#9ca3af', fontSize: 13 }}>Complete</span>;
    }
  }

  function renderStatusBadge(status) {
    const style = statusStyles[status] || statusStyles.PENDING;
    return (
      <span style={{
        display: 'inline-block',
        padding: '4px 10px',
        background: style.bg,
        color: style.text,
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 600
      }}>
        {style.label}
      </span>
    );
  }

  // Filter requests
  const filteredRequests = allRequests.filter(req => {
    if (filter === 'all') return true;
    if (filter === 'active') return ['PENDING', 'APPROVED', 'FUNDED'].includes(req.status);
    return req.status === filter.toUpperCase();
  });

  return (
    <div style={{ maxWidth: 1200 }}>
      <h1 style={{ marginBottom: 24 }}>💵 Quick Pay Administration</h1>

      {error && (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 8, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {/* Stats cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16, marginBottom: 24 }}>
          <div 
            onClick={() => setFilter('pending')}
            style={{ background: '#fef3c7', padding: 20, borderRadius: 12, border: '2px solid #f59e0b', cursor: 'pointer' }}
          >
            <div style={{ fontSize: 14, color: '#92400e' }}>Pending</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#92400e' }}>{stats.pending}</div>
          </div>
          <div 
            onClick={() => setFilter('approved')}
            style={{ background: '#dbeafe', padding: 20, borderRadius: 12, border: '2px solid #3b82f6', cursor: 'pointer' }}
          >
            <div style={{ fontSize: 14, color: '#1e40af' }}>Approved</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#1e40af' }}>{stats.approved}</div>
          </div>
          <div 
            onClick={() => setFilter('funded')}
            style={{ background: '#d1fae5', padding: 20, borderRadius: 12, border: '2px solid #10b981', cursor: 'pointer' }}
          >
            <div style={{ fontSize: 14, color: '#065f46' }}>Funded</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#065f46' }}>{stats.funded}</div>
          </div>
          <div 
            onClick={() => setFilter('settled')}
            style={{ background: '#e0e7ff', padding: 20, borderRadius: 12, border: '2px solid #6366f1', cursor: 'pointer' }}
          >
            <div style={{ fontSize: 14, color: '#3730a3' }}>Settled</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#3730a3' }}>{stats.settled}</div>
          </div>
          <div style={{ background: 'white', padding: 20, borderRadius: 12, border: '2px solid #e5e7eb' }}>
            <div style={{ fontSize: 14, color: '#6b7280' }}>Total Advanced</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#059669' }}>{money(stats.totalAdvancedCents)}</div>
          </div>
          <div style={{ background: 'white', padding: 20, borderRadius: 12, border: '2px solid #e5e7eb' }}>
            <div style={{ fontSize: 14, color: '#6b7280' }}>Total Fees Earned</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#2563eb' }}>{money(stats.totalFeesCents)}</div>
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['active', 'pending', 'approved', 'funded', 'settled', 'all'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: 8,
              background: filter === f ? '#1f2937' : '#f3f4f6',
              color: filter === f ? 'white' : '#374151',
              fontWeight: 600,
              cursor: 'pointer',
              textTransform: 'capitalize'
            }}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Requests table */}
      <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <div style={{ padding: 16, borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>
            {filter === 'active' ? '🔄 Active Requests' : filter === 'all' ? '📋 All Requests' : `${statusStyles[filter.toUpperCase()]?.label || filter} Requests`} ({filteredRequests.length})
          </h2>
        </div>
        
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>
        ) : filteredRequests.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
            No {filter} requests
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 700 }}>Load</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 700 }}>Shipper</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 700 }}>Carrier Tier</th>
                <th style={{ padding: 12, textAlign: 'right', fontWeight: 700 }}>Load Value</th>
                <th style={{ padding: 12, textAlign: 'right', fontWeight: 700 }}>Advance</th>
                <th style={{ padding: 12, textAlign: 'right', fontWeight: 700 }}>Fee</th>
                <th style={{ padding: 12, textAlign: 'right', fontWeight: 700 }}>Net to Carrier</th>
                <th style={{ padding: 12, textAlign: 'center', fontWeight: 700 }}>Status</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 700 }}>Requested</th>
                <th style={{ padding: 12, textAlign: 'center', fontWeight: 700 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.map(req => (
                <tr key={req.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                  <td style={{ padding: 12 }}>
                    <div style={{ fontWeight: 600 }}>{req.load?.referenceNumber || req.loadId?.slice(0, 8)}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      {req.load?.originCity}, {req.load?.originState} → {req.load?.destCity}, {req.load?.destState}
                    </div>
                  </td>
                  <td style={{ padding: 12, fontSize: 14 }}>
                    {req.load?.shipper?.name || '—'}
                  </td>
                  <td style={{ padding: 12 }}>
                    <span style={{ fontWeight: 600 }}>
                      {tierBadges[req.carrierTier]} {req.carrierTier}
                    </span>
                  </td>
                  <td style={{ padding: 12, textAlign: 'right' }}>{money(req.loadAmountCents)}</td>
                  <td style={{ padding: 12, textAlign: 'right' }}>
                    {money(req.advanceAmountCents)}
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>{req.advancePercentage}%</div>
                  </td>
                  <td style={{ padding: 12, textAlign: 'right', color: '#059669', fontWeight: 600 }}>
                    {money(req.feeCents)}
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>{req.feePercentage}%</div>
                  </td>
                  <td style={{ padding: 12, textAlign: 'right', fontWeight: 700 }}>
                    {money(req.netAdvanceCents)}
                  </td>
                  <td style={{ padding: 12, textAlign: 'center' }}>
                    {renderStatusBadge(req.status)}
                  </td>
                  <td style={{ padding: 12, fontSize: 13, color: '#6b7280' }}>
                    {formatDate(req.requestedAt)}
                  </td>
                  <td style={{ padding: 12 }}>
                    {renderActionButtons(req)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
